import { useEffect, useState, useCallback, Fragment } from "react";
import api, { apiError } from "@/lib/api";
import { fmtTL, fmtCur } from "@/components/StatusBadge";
import { Pagination } from "@/components/Pagination";
import QuickDocDialog from "@/components/QuickDocDialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  FileText, Download, AlertTriangle, CheckCircle2, FileClock, RefreshCw, Loader2,
  Send, Eye, FilePlus2, Columns3, X, Plus, Search, GripVertical, Printer, Inbox, ArrowUpRight, Layers,
  Info, RotateCw,
} from "lucide-react";

const KIND_LABELS = { efatura: "e-Fatura", earchive: "e-Arşiv", despatch: "e-İrsaliye" };
const KIND_CLS = {
  efatura: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  earchive: "bg-sky-50 text-sky-700 ring-sky-200",
  despatch: "bg-violet-50 text-violet-700 ring-violet-200",
};
const STATUS = {
  draft: { label: "Taslak", cls: "bg-amber-100 text-amber-800 ring-amber-300", Icon: FileClock },
  sent: { label: "Gönderildi", cls: "bg-emerald-100 text-emerald-800 ring-emerald-300", Icon: CheckCircle2 },
  error: { label: "Hata", cls: "bg-rose-100 text-rose-800 ring-rose-300", Icon: AlertTriangle },
};
const PRESETS = {
  Perakende: ["name", "quantity", "unit", "unit_price", "vat_rate", "line_total"],
  Toptan: ["sku", "name", "quantity", "unit", "unit_price", "vat_rate", "vat_amount", "line_total"],
  İhracat: ["sku", "name", "description", "quantity", "unit", "unit_price", "line_total"],
};

function Kpi({ label, value, tone, testid, Icon }) {
  const tones = {
    green: "text-emerald-950", amber: "text-amber-700", sky: "text-sky-700", rose: "text-rose-700",
  };
  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2.5" data-testid={testid}>
      <div className="flex h-9 w-9 items-center justify-center rounded-md bg-white ring-1 ring-slate-200"><Icon className="h-4 w-4 text-slate-500" /></div>
      <div>
        <p className="text-[11px] font-medium text-slate-500">{label}</p>
        <p className={`font-mono text-xl font-bold tracking-tight ${tones[tone]}`}>{value}</p>
      </div>
    </div>
  );
}

export default function EDocuments() {
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [kindFilter, setKindFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dirFilter, setDirFilter] = useState("outgoing");
  const [qText, setQText] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [quickOpen, setQuickOpen] = useState(false);
  const [pulseId, setPulseId] = useState(null);
  const [openDetail, setOpenDetail] = useState(null);
  const [kpis, setKpis] = useState({ out: 0, drafts: 0, sent: 0, err: 0 });
  const [datePreset, setDatePreset] = useState("");
  const [dFrom, setDFrom] = useState("");
  const [dTo, setDTo] = useState("");
  const { can } = useAuth();

  useEffect(() => { api.get("/e-belge/status").then((r) => setStatus(r.data)).catch(() => {}); }, []);

  const loadKpis = useCallback(async () => {
    const mk = (params) => api.get("/e-belge", { params: { ...params, page_size: 1 } }).then((r) => r.data.total).catch(() => 0);
    const [out, drafts, sent, err] = await Promise.all([
      mk({ direction: "outgoing" }), mk({ direction: "outgoing", status: "draft" }),
      mk({ direction: "outgoing", status: "sent" }), mk({ direction: "outgoing", status: "error" }),
    ]);
    setKpis({ out, drafts, sent, err });
  }, []);
  useEffect(() => { loadKpis(); }, [loadKpis]);

  const refreshIncoming = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/edoc/v2/incoming/refresh");
      if (data.ok) toast.success(data.message || `${data.count} belge bulundu`);
      else toast.warning(data.message || "Gelen belge kontrol edilemedi");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Yenilenemedi"); }
    finally { setRefreshing(false); }
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get("/e-belge", { params: {
      kind: kindFilter || undefined, status: statusFilter || undefined,
      direction: dirFilter || undefined, q_text: qText.trim() || undefined,
      date_from: dFrom || undefined, date_to: dTo || undefined,
      page, page_size: pageSize,
    } })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [kindFilter, statusFilter, dirFilter, qText, dFrom, dTo, page, pageSize]);

  const EDOC_PRESETS = [["today", "Bugün"], ["7d", "Son 7 Gün"], ["thismonth", "Bu Ay"], ["lastmonth", "Geçen Ay"], ["lastyear", "Geçen Yıl"]];
  const applyPreset = (p) => {
    const now = new Date(); const iso = (d) => d.toISOString().slice(0, 10);
    let f = "", t = "";
    if (p === "today") { f = iso(now); t = iso(now); }
    else if (p === "7d") { const s = new Date(now); s.setDate(s.getDate() - 6); f = iso(s); t = iso(now); }
    else if (p === "thismonth") { f = iso(new Date(now.getFullYear(), now.getMonth(), 1)); t = iso(now); }
    else if (p === "lastmonth") { f = iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)); t = iso(new Date(now.getFullYear(), now.getMonth(), 0)); }
    else if (p === "lastyear") { f = iso(new Date(now.getFullYear() - 1, 0, 1)); t = iso(new Date(now.getFullYear() - 1, 11, 31)); }
    setDatePreset(p); setDFrom(f); setDTo(t); setPage(1);
  };
  const clearDates = () => { setDatePreset(""); setDFrom(""); setDTo(""); setPage(1); };
  const exportXlsx = async () => {
    try {
      const res = await api.get("/e-belge/export.xlsx", { params: {
        kind: kindFilter || undefined, status: statusFilter || undefined, direction: dirFilter || undefined,
        q_text: qText.trim() || undefined, date_from: dFrom || undefined, date_to: dTo || undefined,
      }, responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = "e-belgeler.xlsx"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Excel indirilemedi"); }
  };

  useEffect(() => { const t = setTimeout(load, qText ? 300 : 0); return () => clearTimeout(t); }, [load, qText]);

  const onCreated = (data) => {
    load(); loadKpis();
    if (data?.id) { setPulseId(data.id); setTimeout(() => setPulseId(null), 2600); }
  };

  const downloadUbl = async (d) => {
    try {
      const res = await api.get(`/e-belge/${d.id}/ubl`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${d.doc_no}.xml`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "İndirilemedi"); }
  };

  const previewDoc = async (d, print = false) => {
    try {
      const res = await api.get(`/e-belge/${d.id}/preview`, { responseType: "text" });
      const w = window.open("", "_blank");
      if (w) {
        w.document.open(); w.document.write(res.data); w.document.close();
        if (print) { w.focus(); setTimeout(() => { try { w.print(); } catch (e) { /* kullanıcı elle yazdırır */ } }, 400); }
      } else toast.error("Açılır pencere engellendi — izin verin");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Önizleme açılamadı"); }
  };

  const sendNow = async (d) => {
    setBusyId(d.id);
    try {
      const { data } = await api.post(`/e-belge/${d.id}/send`);
      if (data.status === "sent") toast.success(`${data.doc_no} entegratöre gönderildi`);
      else if (data.status === "error") toast.error(`Gönderilemedi: ${data.error || data.note_status || ""}`);
      else toast.info(data.note_status || "Aktif bağlantı yok / onay bekliyor — taslak olarak kaldı");
      load(); loadKpis();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Gönderilemedi"); }
    finally { setBusyId(null); }
  };

  const setKind = (v) => { setKindFilter(v); setPage(1); };
  const setStat = (v) => { setStatusFilter(v); setPage(1); };
  const setDir = (v) => { setDirFilter(v); setPage(1); };

  const incomingEmpty = dirFilter === "incoming" && !loading && items.length === 0;

  return (
    <div className="space-y-4 fade-in" data-testid="edocuments-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-brand-green" />
          <div><p className="overline">Muhasebe</p><h1 className="font-heading text-2xl font-bold text-slate-900">E-Belge Merkezi</h1></div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="edoc-refresh-incoming" onClick={refreshIncoming} disabled={refreshing}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Gelen Belgeleri Yenile
          </button>
          {can("edoc.manual") && (
            <button data-testid="edoc-quick-new" onClick={() => setQuickOpen(true)}
              className="inline-flex shrink-0 items-center gap-2 rounded-md border border-emerald-900 bg-emerald-800 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-700 active:scale-95">
              <FilePlus2 className="h-4 w-4" /> Belge Üret
            </button>
          )}
        </div>
      </div>

      {/* KPI şeridi */}
      <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm md:grid-cols-4">
        <Kpi label="Giden Belge (Toplam)" value={kpis.out} tone="green" Icon={ArrowUpRight} testid="edoc-kpi-out" />
        <Kpi label="Taslaklar" value={kpis.drafts} tone="amber" Icon={FileClock} testid="edoc-kpi-draft" />
        <Kpi label="Gönderildi" value={kpis.sent} tone="green" Icon={CheckCircle2} testid="edoc-kpi-sent" />
        <Kpi label="Hatalı / Bekleyen" value={kpis.err} tone="rose" Icon={AlertTriangle} testid="edoc-kpi-error" />
      </div>

      {status && !status.configured && (
        <div data-testid="edoc-config-banner" className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-xs text-amber-800 ring-1 ring-inset ring-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <b>Entegratör bağlı değil — Taslak mod.</b> EDM Bilişim / İzibiz bilgileri girilene kadar belgeler UBL-TR taslağı olarak üretilip saklanır. Bilgiler <b>Yönetim → Entegrasyonlar → E-Belge</b> ekranından girilince GİB'e otomatik gönderim aktifleşir.
            {status.missing?.length > 0 && <span className="ml-1 text-amber-600">Eksik: {status.missing.join(", ")}</span>}
          </div>
        </div>
      )}
      {status && status.configured && (
        <div className="flex items-center gap-2 rounded-md bg-emerald-50 p-3 text-xs text-emerald-800 ring-1 ring-inset ring-emerald-200">
          <CheckCircle2 className="h-4 w-4 shrink-0" /> Entegratör aktif: <b className="capitalize">{status.provider}</b> · Taslakları "Gönder" ile GİB'e iletebilirsiniz.
        </div>
      )}

      <EdocColumnsCard />

      {/* İki boyutlu filtre çubuğu */}
      <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-100/80 p-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-1" data-testid="edoc-type-tabs">
            {[{ v: "", l: "Tümü" }, { v: "efatura", l: "e-Fatura" }, { v: "earchive", l: "e-Arşiv" }, { v: "despatch", l: "e-İrsaliye" }].map((o) => (
              <button key={o.v} data-testid={`edoc-type-${o.v || "all"}`} onClick={() => setKind(o.v)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${kindFilter === o.v ? "bg-emerald-800 text-white shadow-sm" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"}`}>{o.l}</button>
            ))}
          </div>
          <div className="inline-flex overflow-hidden rounded-md ring-1 ring-inset ring-slate-300" data-testid="edoc-dir-seg">
            {[{ v: "outgoing", l: "Giden", Icon: ArrowUpRight }, { v: "incoming", l: "Gelen", Icon: Inbox }, { v: "", l: "Tümü", Icon: Layers }].map((o) => (
              <button key={o.v || "all"} data-testid={`edoc-dir-${o.v || "all"}`} onClick={() => setDir(o.v)}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold transition-colors ${dirFilter === o.v ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                <o.Icon className="h-3.5 w-3.5" /> {o.l}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1">
            {[{ v: "", l: "Tüm durum" }, { v: "draft", l: "Taslak" }, { v: "sent", l: "Gönderildi" }, { v: "error", l: "Hata" }].map((o) => (
              <button key={o.v || "all"} data-testid={`edoc-status-${o.v || "all"}`} onClick={() => setStat(o.v)}
                className={`rounded-md px-2.5 py-1 text-[11px] font-semibold transition-colors ${statusFilter === o.v ? "bg-slate-700 text-white" : "bg-white text-slate-500 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"}`}>{o.l}</button>
            ))}
          </div>
        </div>
        <div className="relative md:w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="edoc-search" value={qText} onChange={(e) => { setQText(e.target.value); setPage(1); }}
            placeholder="Belge no / müşteri / VKN ara…" className="h-9 w-full rounded-md border border-slate-200 bg-white pl-8 pr-3 text-sm outline-none focus:ring-2 focus:ring-emerald-600" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-3" data-testid="edoc-date-bar">
        <span className="text-xs font-semibold text-slate-500">Tarih:</span>
        {EDOC_PRESETS.map(([v, l]) => (
          <button key={v} data-testid={`edoc-date-${v}`} onClick={() => applyPreset(v)}
            className={`rounded-md px-2.5 py-1 text-[11px] font-semibold ${datePreset === v ? "bg-emerald-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
        ))}
        <input type="date" data-testid="edoc-date-from" value={dFrom} onChange={(e) => { setDFrom(e.target.value); setDatePreset(""); setPage(1); }} className="h-8 rounded-md border border-slate-300 px-2 text-xs" />
        <span className="text-xs text-slate-400">—</span>
        <input type="date" data-testid="edoc-date-to" value={dTo} onChange={(e) => { setDTo(e.target.value); setDatePreset(""); setPage(1); }} className="h-8 rounded-md border border-slate-300 px-2 text-xs" />
        {(dFrom || dTo) && <button data-testid="edoc-date-clear" onClick={clearDates} className="text-[11px] text-slate-400 hover:text-red-600">Temizle</button>}
        <button data-testid="edoc-export-xlsx" onClick={exportXlsx} className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"><Download className="h-4 w-4" /> Excel'e Aktar</button>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-inset ring-slate-200">
        <table className="w-full text-sm">
          <thead className="border-b border-emerald-800/15 bg-emerald-800/[0.06] text-left text-xs font-semibold uppercase tracking-wide text-emerald-900/70">
            <tr>
              <th className="px-4 py-3">Belge No</th>
              <th className="px-4 py-3">Tür</th>
              <th className="px-4 py-3">Kaynak</th>
              <th className="px-4 py-3">Lokasyon</th>
              <th className="px-4 py-3">Müşteri / Alıcı</th>
              <th className="px-4 py-3 text-right">Tutar</th>
              <th className="px-4 py-3">Durum</th>
              <th className="px-4 py-3">Tarih</th>
              <th className="px-4 py-3 text-right">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">Yükleniyor...</td></tr>}
            {incomingEmpty && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400"><Inbox className="mx-auto mb-2 h-8 w-8 text-slate-300" />Gelen belge yok. Entegratör gelen belge çekmeyi destekliyorsa "Gelen Belgeleri Yenile" ile kontrol edin.</td></tr>}
            {!loading && !incomingEmpty && items.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Belge bulunamadı. "Belge Üret" ile yeni fatura/irsaliye oluşturabilirsiniz.</td></tr>}
            {!loading && items.map((d) => {
              const st = STATUS[d.status] || STATUS.draft;
              const pulsing = d.id === pulseId;
              const hasDetail = d.status === "error" || !!d.error || !!d.note_status;
              const detailOpen = openDetail === d.id;
              return (
                <Fragment key={d.id}>
                <tr data-testid={`edoc-row-${d.id}`} className={`transition-colors ${pulsing ? "animate-pulse bg-emerald-50 ring-2 ring-inset ring-emerald-500" : detailOpen ? "bg-emerald-50/40" : "odd:bg-white even:bg-slate-50/50 hover:bg-emerald-50/60"}`}>
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-700">{d.doc_no}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${KIND_CLS[d.kind] || ""}`}>{KIND_LABELS[d.kind] || d.kind}</span></td>
                  <td className="px-4 py-3 font-mono text-[11px] text-slate-500">{d.source_no || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{d.location_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{d.customer_name || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-800">{d.totals ? fmtCur(d.totals.grand_total, d.currency || "TRY") : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${st.cls}`}><st.Icon className="h-3 w-3" />{st.label}</span>
                      {hasDetail && (
                        <button data-testid={`edoc-why-${d.id}`} onClick={() => setOpenDetail(detailOpen ? null : d.id)} title="Ayrıntı / neden"
                          className={`inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-semibold transition-colors ${d.status === "error" ? "text-rose-600 hover:bg-rose-50" : "text-slate-400 hover:bg-slate-100"}`}>
                          <Info className="h-3 w-3" /> {detailOpen ? "Gizle" : "Neden?"}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{new Date(d.created_at).toLocaleString("tr-TR")}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {(d.status === "draft" || d.status === "error") && d.direction !== "incoming" && (
                        <button data-testid={`edoc-send-${d.id}`} onClick={() => sendNow(d)} disabled={busyId === d.id}
                          title="Aktif entegratöre gönder" className="inline-flex items-center gap-1 rounded-md bg-emerald-800 px-2 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
                          {busyId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Gönder
                        </button>
                      )}
                      <button data-testid={`edoc-preview-${d.id}`} onClick={() => previewDoc(d)} title="Belgeyi görüntüle"
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50">
                        <Eye className="h-3.5 w-3.5" /> Gör
                      </button>
                      <button data-testid={`edoc-print-${d.id}`} onClick={() => previewDoc(d, true)} title="PDF olarak yazdır / indir"
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50">
                        <Printer className="h-3.5 w-3.5" /> PDF
                      </button>
                      <button data-testid={`edoc-ubl-${d.id}`} onClick={() => downloadUbl(d)} title="UBL-TR (XML) indir"
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50">
                        <Download className="h-3.5 w-3.5" /> UBL
                      </button>
                    </div>
                  </td>
                </tr>
                {detailOpen && (
                  <tr data-testid={`edoc-detail-${d.id}`} className="bg-slate-50/80">
                    <td colSpan={9} className="px-4 pb-3 pt-0">
                      <div className="space-y-2 rounded-md border border-slate-200 bg-white p-3">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${d.status === "error" ? "text-rose-500" : "text-amber-500"}`} />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-bold text-slate-800">{d.status === "error" ? "Gönderim başarısız oldu" : "Durum bilgisi"}</p>
                            <p className="mt-0.5 break-words text-xs text-slate-600" data-testid={`edoc-detail-msg-${d.id}`}>{d.error || d.note_status || "Ek ayrıntı yok."}</p>
                            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[10px] text-slate-400">
                              {d.provider && <span>Entegratör: <b className="text-slate-500">{d.provider}</b></span>}
                              {d.environment && <span>Ortam: <b className="text-slate-500">{d.environment}</b></span>}
                              {d.normalized_status && <span>Durum kodu: <b className="text-slate-500">{d.normalized_status}</b></span>}
                              {d.correlation_id && <span>İzleme No: <b className="font-mono text-slate-500">{d.correlation_id}</b></span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2">
                          {(d.status === "error" || d.status === "draft") && d.direction !== "incoming" && (
                            <button data-testid={`edoc-retry-${d.id}`} onClick={() => sendNow(d)} disabled={busyId === d.id}
                              className="inline-flex items-center gap-1 rounded-md bg-emerald-800 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
                              {busyId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />} Tekrar Dene
                            </button>
                          )}
                          <button data-testid={`edoc-detail-ubl-${d.id}`} onClick={() => downloadUbl(d)}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"><Download className="h-3.5 w-3.5" /> UBL indir</button>
                          <span className="text-[10px] text-slate-400">İpucu: Bilgileri kontrol edip tekrar deneyin. Sorun sürerse Yönetim → Entegrasyonlar → E-Belge ayarlarını kontrol edin.</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pageSize={pageSize} total={total} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} testid="edoc-pagination" />
      <QuickDocDialog open={quickOpen} onOpenChange={setQuickOpen} onCreated={onCreated} />
    </div>
  );
}

function EdocColumnsCard() {
  const { can } = useAuth();
  const editable = can("design.manage");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newCustom, setNewCustom] = useState("");
  useEffect(() => { api.get("/edoc/columns").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const cats = [{ k: "invoice", l: "Fatura / e-Arşiv" }, { k: "despatch", l: "e-İrsaliye" }];
  const setCfg = (cat, list) => setData((d) => ({ ...d, config: { ...d.config, [cat]: list } }));
  const meta = (cat, key) => (data.catalog[cat].find((c) => c.key === key) || {});
  const toggle = (cat, key) => {
    const cur = data.config[cat] || [];
    if (cur.includes(key)) setCfg(cat, cur.filter((x) => x !== key));
    else setCfg(cat, [...cur, key]);
  };
  const onDragEnd = (r) => {
    if (!r.destination || r.source.droppableId !== r.destination.droppableId) return;
    const cat = r.source.droppableId;
    const cur = [...(data.config[cat] || [])];
    const [m] = cur.splice(r.source.index, 1);
    cur.splice(r.destination.index, 0, m);
    setCfg(cat, cur);
  };
  const addCustom = () => {
    const label = newCustom.trim();
    if (!label) return;
    const key = "custom_" + (label.toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 18) || "col") + "_" + (data.custom?.length || 0);
    const entry = { key, label, always: false, custom: true };
    setData((d) => ({
      ...d, custom: [...(d.custom || []), { key, label }],
      catalog: { invoice: [...d.catalog.invoice, entry], despatch: [...d.catalog.despatch, entry] },
    }));
    setNewCustom("");
  };
  const delCustom = (key) => setData((d) => ({
    ...d,
    custom: (d.custom || []).filter((c) => c.key !== key),
    catalog: { invoice: d.catalog.invoice.filter((c) => c.key !== key), despatch: d.catalog.despatch.filter((c) => c.key !== key) },
    config: { invoice: d.config.invoice.filter((k) => k !== key), despatch: d.config.despatch.filter((k) => k !== key) },
  }));
  const applyPreset = (name) => setCfg("invoice", PRESETS[name]);
  const save = async () => {
    setSaving(true);
    try {
      const { data: v } = await api.put("/edoc/columns", { ...data.config, custom: data.custom });
      setData(v);
      toast.success("Belge kolonları kaydedildi");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Kaydedilemedi (yalnızca Yönetici)"); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-md bg-white ring-1 ring-inset ring-indigo-200" data-testid="edoc-columns-card">
      <button data-testid="edoc-columns-toggle" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between px-4 py-3 text-left">
        <span className="flex items-center gap-2">
          <Columns3 className="h-4 w-4 text-indigo-600" />
          <span className="font-heading text-sm font-bold text-slate-800">Belge Kolonları (Sürükle-Sırala & Özel Kolon)</span>
        </span>
        <span className="text-xs font-semibold text-indigo-600">{open ? "Gizle" : "Düzenle"}</span>
      </button>
      {open && (
        <div className="space-y-4 border-t border-slate-100 p-4">
          <p className="text-xs text-slate-500">Kolonları sürükleyerek sırala; ekle/kaldır. "Mal/Hizmet" ve "Miktar" zorunludur. Değişiklik belge önizleme/çıktısına yansır.</p>
          {editable && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-slate-500">Hazır Şablon (Fatura):</span>
              {Object.keys(PRESETS).map((n) => (
                <button key={n} type="button" data-testid={`edoc-preset-${n}`} onClick={() => applyPreset(n)}
                  className="rounded-full border border-slate-300 px-3 py-0.5 text-[11px] font-medium text-slate-600 hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-700">{n}</button>
              ))}
            </div>
          )}
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="grid gap-4 lg:grid-cols-2">
              {cats.map(({ k, l }) => (
                <div key={k} className="rounded-lg border border-slate-200 p-3" data-testid={`edoc-columns-cat-${k}`}>
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">{l}</p>
                  <Droppable droppableId={k} isDropDisabled={!editable}>
                    {(prov) => (
                      <div ref={prov.innerRef} {...prov.droppableProps} className="space-y-1.5">
                        {(data.config[k] || []).map((key, idx) => (
                          <Draggable key={key} draggableId={`${k}-${key}`} index={idx} isDragDisabled={!editable}>
                            {(dp, snap) => (
                              <div ref={dp.innerRef} {...dp.draggableProps} data-testid={`edoc-col-enabled-${k}-${key}`}
                                className={`flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-sm ${snap.isDragging ? "border-emerald-400 bg-emerald-50 shadow" : "border-slate-100 bg-slate-50"}`}>
                                {editable && <span {...dp.dragHandleProps} data-testid={`edoc-col-grip-${k}-${key}`} className="cursor-grab text-slate-300 hover:text-slate-500 active:cursor-grabbing"><GripVertical className="h-4 w-4" /></span>}
                                <span className="flex-1 font-medium text-slate-700">{meta(k, key).label || key}
                                  {meta(k, key).always && <span className="ml-1 text-[10px] text-slate-400">(zorunlu)</span>}
                                  {meta(k, key).custom && <span className="ml-1 rounded bg-indigo-100 px-1 text-[9px] font-bold text-indigo-600">ÖZEL</span>}
                                </span>
                                {editable && !meta(k, key).always && <button type="button" data-testid={`edoc-col-remove-${k}-${key}`} onClick={() => toggle(k, key)} className="rounded p-0.5 text-slate-400 hover:bg-red-50 hover:text-red-500"><X className="h-4 w-4" /></button>}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {prov.placeholder}
                      </div>
                    )}
                  </Droppable>
                  {editable && data.catalog[k].some((c) => !(data.config[k] || []).includes(c.key)) && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {data.catalog[k].filter((c) => !(data.config[k] || []).includes(c.key)).map((c) => (
                        <button key={c.key} type="button" data-testid={`edoc-col-add-${k}-${c.key}`} onClick={() => toggle(k, c.key)}
                          className="inline-flex items-center gap-1 rounded-full border border-dashed border-indigo-300 px-2 py-0.5 text-[11px] font-medium text-indigo-600 hover:bg-indigo-50">
                          <Plus className="h-3 w-3" /> {c.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </DragDropContext>

          {editable && (
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Özel Kolon Ekle</p>
              <div className="flex flex-wrap items-center gap-2">
                <input data-testid="edoc-col-custom-input" value={newCustom} onChange={(e) => setNewCustom(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") addCustom(); }}
                  placeholder="Örn. GTİP No, Menşei, Seri No…" className="h-9 flex-1 rounded-md border border-slate-200 px-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
                <button type="button" data-testid="edoc-col-custom-add" onClick={addCustom}
                  className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700"><Plus className="h-3.5 w-3.5" /> Ekle</button>
              </div>
              {(data.custom || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(data.custom || []).map((c) => (
                    <span key={c.key} data-testid={`edoc-col-custom-${c.key}`} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200">
                      {c.label}
                      <button type="button" data-testid={`edoc-col-custom-del-${c.key}`} onClick={() => delCustom(c.key)} className="rounded-full p-0.5 hover:bg-red-50 hover:text-red-500"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              <p className="mt-1.5 text-[10px] text-slate-400">Özel kolonu ekledikten sonra ilgili kategoriye "+ ile" ekleyin ve Kaydet'e basın. Belge kesişinde bu kolona satır bazlı değer girebilirsiniz.</p>
            </div>
          )}

          {editable ? (
            <button data-testid="edoc-columns-save" onClick={save} disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-800 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Kaydet
            </button>
          ) : <p className="text-[11px] text-amber-600">Yalnızca Yönetici değiştirebilir.</p>}
        </div>
      )}
    </div>
  );
}
